import type { Cell, Rule } from '../shared/protocol';

// The DMZ app's API (src/HphDashboard.Mobile/PublicApi.cs). Same origin, cookie session, and the
// X-HphDash header on every write.

export interface MobileSite {
  id: string;
  name: string;
}

export interface MobileCard {
  id: string;
  kind: 'table' | 'kpi' | 'text';
  title: string;
  query?: string;
  text?: string;
  display?: Record<string, unknown>;
  columns?: { hidden?: string[]; labels?: Record<string, string>; align?: Record<string, string>; order?: string[] };
  rules?: Rule[];
  feeds: Record<string, string>;
}

export interface OverviewColumn {
  card: string;
  label: string;
  group?: string;
}

/** Rows of another publication in an overview: its card for each column (null = none). */
export interface OverviewSource {
  publication: string;
  cards: (string | null)[];
}

/** One table over the publication's sites: a row per site, the chosen cards' numbers as columns. */
export interface Overview {
  title: string;
  columns: OverviewColumn[];
  messages: string[];
  totals: boolean;
  others?: OverviewSource[];
}

export interface Publication {
  id: string;
  title: string;
  description?: string;
  sort: number;
  sites: MobileSite[];
  cards: MobileCard[];
  overview?: Overview;
}

export interface Prefs {
  /** What opens when the app starts: '' = the list, 'd:<id>' = a dashboard, 'o:<id>' = its overview. */
  start?: string;
  site?: string;
  favorites?: string[];
  order?: Record<string, string[]>;
  hidden?: Record<string, string[]>;
  text?: 's' | 'm' | 'l';
  theme?: 'auto' | 'light' | 'dark';
}

export interface Catalog {
  user: string;
  fullName: string;
  /** One of the phone app's admins: the user list (Felhasználók) is theirs. */
  isAdmin?: boolean;
  /** An authenticator app is set up for this user. */
  totp?: boolean;
  prefs: Prefs | null;
  scriptsVersion: number;
  lastSyncAt?: number;
  publications: Publication[];
}

export interface Frame {
  feed: string;
  at: number;
  status: string;
  error?: string;
  since?: number;
  cols: [string, string][];
  rows: Cell[][];
  total: number;
  trunc?: boolean;
}

export interface DataResponse {
  now: number;
  frames: Record<string, Frame>;
}

export class Unauthorized extends Error {}

/** A server answer other than 2xx/304/401: its message, and the status (410 = the login has to start over). */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** After the password: signed in, or the authenticator step ('code'), the first time with its setup ('enroll'). */
export interface LoginReply {
  user?: string;
  fullName?: string;
  step?: 'code' | 'enroll';
  ticket?: string;
  /** Setup only: the key to type in by hand, the otpauth:// link, the QR code as a data URL. */
  key?: string;
  uri?: string;
  qr?: string;
}

export interface AdminUser {
  user: string;
  name: string;
  mustChange: boolean;
  sessions: number;
  lastSeen?: number;
  lastLogin?: number;
  failed24h: number;
  totpSince?: number;
  admin: boolean;
  totpRequired: boolean;
}

export interface AdminUsers {
  now: number;
  totp: 'off' | 'admins' | 'all';
  users: AdminUser[];
}

/** The server is out of reach: no network, no answer in time, or nginx answering for it. */
export class Offline extends Error {}

// nginx answers 502/503/504 when it cannot reach the DMZ app; the app itself never sends them.
const GATEWAY = new Set([502, 503, 504]);
// Give up well before the 20 s poll, so the last saved data shows instead of a long wait.
const TIMEOUT_MS = 15_000;

async function request<T>(method: 'GET' | 'POST', url: string, body?: unknown, etag?: string): Promise<{ status: number; data: T | null; etag: string | null }> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method === 'POST') {
    headers['X-HphDash'] = '1';
    headers['Content-Type'] = 'application/json';
  }
  if (etag) headers['If-None-Match'] = etag;

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  let res: Response;
  let text: string;
  try {
    res = await fetch(url, { method, headers, credentials: 'same-origin', cache: 'no-store', signal: abort.signal, body: body === undefined ? undefined : JSON.stringify(body) });
    if (GATEWAY.has(res.status)) throw new Offline('A szerver nem érhető el');
    text = res.status === 304 ? '' : await res.text();
  } catch (e) {
    throw e instanceof Offline ? e : new Offline('Nincs kapcsolat');
  } finally {
    clearTimeout(timer);
  }
  if (res.status === 304) return { status: 304, data: null, etag: etag ?? null };
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* not JSON */
  }
  // 401 is both "session over" and "wrong password"; the server's message tells which.
  if (res.status === 401) throw new Unauthorized((data && data.error) || 'Bejelentkezés szükséges');
  if (!res.ok) throw new ApiError((data && data.error) || res.statusText || 'Hiba', res.status);
  return { status: res.status, data: data as T, etag: res.headers.get('ETag') };
}

export const api = {
  login: (userName: string, password: string) => request<LoginReply>('POST', '/api/login', { userName, password }),
  loginCode: (ticket: string, code: string) => request<LoginReply>('POST', '/api/login/code', { ticket, code }),
  logout: () => request('POST', '/api/logout', {}),
  catalog: () => request<Catalog>('GET', '/api/catalog'),
  data: (pub: string, site: string, etag?: string) =>
    request<DataResponse>('GET', `/api/data?pub=${encodeURIComponent(pub)}&site=${encodeURIComponent(site)}`, undefined, etag),
  overview: (pub: string, etag?: string) => request<DataResponse>('GET', `/api/overview?pub=${encodeURIComponent(pub)}`, undefined, etag),
  savePrefs: (prefs: Prefs) => request('POST', '/api/prefs', prefs),
  adminUsers: () => request<AdminUsers>('GET', '/api/admin/users'),
  adminLogout: (userName: string) => request<{ ended: number }>('POST', '/api/admin/users/logout', { userName }),
  adminResetTotp: (userName: string) => request<{ had: boolean; ended: number }>('POST', '/api/admin/users/reset-totp', { userName }),
};

// ── last known data, for opening the app without a connection ─────────────
// Browser storage is a convenience here: any access may throw, and the app works without it.

export function remember(key: string, value: unknown): void {
  try {
    localStorage.setItem('hphm.' + key, JSON.stringify(value));
  } catch {
    /* full or blocked */
  }
}

export function recall<T>(key: string): T | null {
  try {
    const s = localStorage.getItem('hphm.' + key);
    return s ? (JSON.parse(s) as T) : null;
  } catch {
    return null;
  }
}

export function forget(prefix: string): void {
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith('hphm.' + prefix)) localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}
